/**
 * useJobNotifications Hook
 *
 * React hook that integrates toast notifications with the BackgroundJobsContext.
 * Automatically shows toasts when jobs start, complete, fail, or update progress.
 *
 * @module hooks/useJobNotifications
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { notifyJobStart, notifyJobComplete, notifyJobError } = useJobNotifications();
 *
 *   const handleStartJob = async () => {
 *     const jobId = await startJob(projectId, researchOverview);
 *     notifyJobStart(jobId, 'Healthcare Tech', 5);
 *   };
 * }
 * ```
 */

import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  showJobStartToast,
  showJobCompleteToast,
  showJobErrorToast,
  showJobProgressToast,
  dismissToast,
  ReportMetrics,
  JobError,
  JobProgress,
} from '../lib/toast';
import { useBackgroundJobs } from '../contexts/BackgroundJobsContext';

// ============================================================================
// TYPES
// ============================================================================

interface UseJobNotificationsReturn {
  /**
   * Show a toast when a job starts
   * @param jobId - Unique job identifier
   * @param sector - Sector name (e.g., "Healthcare Tech")
   * @param estimatedMinutes - Estimated completion time in minutes
   * @param projectId - Optional project ID for "View Status" navigation
   */
  notifyJobStart: (
    jobId: string,
    sector: string,
    estimatedMinutes: number,
    projectId?: string
  ) => void;

  /**
   * Show a toast when a job completes successfully
   * @param reportId - Unique report identifier
   * @param sector - Sector name
   * @param metrics - Key report metrics to display
   * @param projectId - Project ID for navigation
   */
  notifyJobComplete: (
    reportId: string,
    sector: string,
    metrics: ReportMetrics,
    projectId: string
  ) => void;

  /**
   * Show a toast when a job fails
   * @param jobId - Unique job identifier
   * @param sector - Sector name
   * @param error - Error details
   * @param projectId - Optional project ID for retry/edit actions
   * @param onRetry - Optional custom retry handler
   */
  notifyJobError: (
    jobId: string,
    sector: string,
    error: JobError,
    projectId?: string,
    onRetry?: () => void
  ) => void;

  /**
   * Show/update a progress toast for an active job
   * @param jobId - Unique job identifier
   * @param sector - Sector name
   * @param progress - Current progress details
   * @param projectId - Optional project ID for cancel action
   */
  notifyJobProgress: (
    jobId: string,
    sector: string,
    progress: JobProgress,
    projectId?: string
  ) => void;

  /**
   * Dismiss all toasts for a specific job
   * @param jobId - Unique job identifier
   */
  dismissJobToasts: (jobId: string) => void;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Hook that provides toast notification functions for background jobs
 *
 * Integrates with BackgroundJobsContext to automatically track active jobs
 * and show appropriate toasts at each stage of the job lifecycle.
 *
 * Features:
 * - Automatic toast management (show, update, dismiss)
 * - Navigation integration (routes to job status/report pages)
 * - Progress tracking with real-time updates
 * - Error handling with contextual actions
 * - Memory leak prevention (cleanup on unmount)
 *
 * @returns Object with notification functions
 */
export function useJobNotifications(): UseJobNotificationsReturn {
  const navigate = useNavigate();
  const { activeJobs } = useBackgroundJobs();

  // Track active toast IDs to prevent duplicates and enable dismissal
  const activeToastIds = useRef<Map<string, string | number>>(new Map());

  // ========================================================================
  // NOTIFICATION FUNCTIONS
  // ========================================================================

  /**
   * Show a toast notification when a job starts
   */
  const notifyJobStart = useCallback(
    (
      jobId: string,
      sector: string,
      estimatedMinutes: number,
      projectId?: string
    ) => {
      // Dismiss any existing toast for this job
      const existingToastId = activeToastIds.current.get(`start-${jobId}`);
      if (existingToastId) {
        dismissToast(existingToastId);
      }

      // Show new toast
      const toastId = showJobStartToast(
        jobId,
        sector,
        estimatedMinutes,
        projectId
          ? () => {
              navigate(`/project/${projectId}?tab=sector-research`);
            }
          : undefined
      );

      // Track toast ID
      activeToastIds.current.set(`start-${jobId}`, toastId);

      // Auto-remove from tracking after toast dismisses (4 seconds + buffer)
      setTimeout(() => {
        activeToastIds.current.delete(`start-${jobId}`);
      }, 5000);
    },
    [navigate]
  );

  /**
   * Show a toast notification when a job completes
   */
  const notifyJobComplete = useCallback(
    (
      reportId: string,
      sector: string,
      metrics: ReportMetrics,
      projectId: string
    ) => {
      // Dismiss any progress toast for this job
      const progressToastId = activeToastIds.current.get(`progress-${reportId}`);
      if (progressToastId) {
        dismissToast(progressToastId);
        activeToastIds.current.delete(`progress-${reportId}`);
      }

      // Show completion toast
      const toastId = showJobCompleteToast(
        reportId,
        sector,
        metrics,
        // Quick Preview - navigate to report page
        () => {
          navigate(`/project/${projectId}?tab=sector-research`);
        },
        // View Report - navigate to report page
        () => {
          navigate(`/project/${projectId}?tab=sector-research`);
        }
      );

      // Track toast ID
      activeToastIds.current.set(`complete-${reportId}`, toastId);

      // Auto-remove from tracking after toast dismisses (12 seconds + buffer)
      setTimeout(() => {
        activeToastIds.current.delete(`complete-${reportId}`);
      }, 13000);
    },
    [navigate]
  );

  /**
   * Show a toast notification when a job fails
   */
  const notifyJobError = useCallback(
    (
      jobId: string,
      sector: string,
      error: JobError,
      projectId?: string,
      onRetry?: () => void
    ) => {
      // Dismiss any progress toast for this job
      const progressToastId = activeToastIds.current.get(`progress-${jobId}`);
      if (progressToastId) {
        dismissToast(progressToastId);
        activeToastIds.current.delete(`progress-${jobId}`);
      }

      // Show error toast
      const toastId = showJobErrorToast(
        jobId,
        sector,
        error,
        onRetry, // Custom retry handler if provided
        projectId
          ? () => {
              // Navigate back to project to edit query
              navigate(`/project/${projectId}?tab=sector-research`);
            }
          : undefined
      );

      // Track toast ID (persists until user dismisses)
      activeToastIds.current.set(`error-${jobId}`, toastId);
    },
    [navigate]
  );

  /**
   * Show/update a progress toast for an active job
   */
  const notifyJobProgress = useCallback(
    (
      jobId: string,
      sector: string,
      progress: JobProgress,
      projectId?: string
    ) => {
      // Check if progress toast already exists
      const existingToastId = activeToastIds.current.get(`progress-${jobId}`);

      if (existingToastId) {
        // Dismiss and recreate (Sonner doesn't support updates natively)
        dismissToast(existingToastId);
      }

      // Show new/updated progress toast
      const toastId = showJobProgressToast(
        jobId,
        sector,
        progress,
        projectId
          ? () => {
              // Cancel job logic would go here
              console.log(`Cancel job ${jobId}`);
            }
          : undefined
      );

      // Track toast ID
      activeToastIds.current.set(`progress-${jobId}`, toastId);
    },
    []
  );

  /**
   * Dismiss all toasts for a specific job
   */
  const dismissJobToasts = useCallback((jobId: string) => {
    // Dismiss all toast types for this job
    const prefixes = ['start-', 'progress-', 'complete-', 'error-'];

    prefixes.forEach((prefix) => {
      const toastId = activeToastIds.current.get(`${prefix}${jobId}`);
      if (toastId) {
        dismissToast(toastId);
        activeToastIds.current.delete(`${prefix}${jobId}`);
      }
    });
  }, []);

  // ========================================================================
  // AUTOMATIC PROGRESS TRACKING
  // ========================================================================

  /**
   * Automatically show progress toasts for active jobs
   *
   * Monitors activeJobs from BackgroundJobsContext and updates
   * progress toasts in real-time.
   */
  useEffect(() => {
    activeJobs.forEach((job) => {
      // Only show progress for jobs that are actively processing
      const isProcessing = [
        'initializing',
        'queued',
        'identifying',
        'researching',
        'analyzing',
        'synthesizing',
        'processing',
      ].includes(job.status);

      if (isProcessing) {
        // Use the requested overview as the sector label when available
        const sector = job.researchOverview || 'Sector Research';

        // Calculate estimated remaining time based on progress and elapsed time
        let estimatedSeconds: number | undefined;
        if (job.startedAt && job.progressPercent > 0) {
          const startedAtMs = job.startedAt.toMillis();
          const elapsedMs = Date.now() - startedAtMs;
          const elapsedSeconds = elapsedMs / 1000;

          // Estimate total time based on current progress, then calculate remaining
          const estimatedTotalSeconds = elapsedSeconds / (job.progressPercent / 100);
          estimatedSeconds = Math.max(0, Math.round(estimatedTotalSeconds - elapsedSeconds));
        }

        notifyJobProgress(
          job.id,
          sector,
          {
            percent: job.progressPercent,
            message: job.progressMessage,
            estimatedSeconds,
          },
          job.projectId
        );
      }
    });
  }, [activeJobs, notifyJobProgress]);

  // ========================================================================
  // CLEANUP ON UNMOUNT
  // ========================================================================

  useEffect(() => {
    return () => {
      // Dismiss all toasts when component unmounts
      activeToastIds.current.forEach((toastId) => {
        dismissToast(toastId);
      });
      activeToastIds.current.clear();
    };
  }, []);

  // ========================================================================
  // RETURN
  // ========================================================================

  return {
    notifyJobStart,
    notifyJobComplete,
    notifyJobError,
    notifyJobProgress,
    dismissJobToasts,
  };
}

// ============================================================================
// HELPER: EXTRACT METRICS FROM REPORT
// ============================================================================

/**
 * Extracts display metrics from a sector research report
 *
 * @param report - Full sector research report
 * @returns Formatted metrics for toast display
 *
 * @example
 * ```typescript
 * const metrics = extractReportMetrics(report);
 * notifyJobComplete(reportId, sector, metrics, projectId);
 * ```
 */
export function extractReportMetrics(report: any): ReportMetrics {
  // Extract market sizing data
  const marketSize = report.marketSizing?.tam?.value
    ? `$${(report.marketSizing.tam.value / 1e9).toFixed(1)}B`
    : 'N/A';

  const growthRate = report.marketSizing?.tam?.cagr
    ? `${(report.marketSizing.tam.cagr * 100).toFixed(1)}% CAGR`
    : 'N/A';

  const targetCount = report.competitiveLandscape?.competitors?.length || 0;

  const keyInsight =
    report.thesisValidation?.overallAssessment?.summary?.slice(0, 100) || undefined;

  return {
    marketSize,
    growthRate,
    targetCount,
    keyInsight,
  };
}
